import { FC } from "react";
import { twMerge } from "tailwind-merge";

import { PageAccordionSection } from "./PageAccordionSection";
import { SectionTitle } from "./SectionTitle";
import { TopicSection } from "./TopicSection";

import { t } from "i18n/i18n";
import { LongFormPencilIcon, PaperClipLinkIcon } from "mds/icons";
import { useAppSelector } from "store/index";
import {
  selectCurrentSectionKind,
  selectPagesForCurrentSection,
  selectVisibleTopicsWithPages,
} from "store/selectors";
import { useListSelector } from "store/store-hooks";

export interface ManageSectionPagesContentProps {
  className?: string;
  scrollAreaClassName?: string;
  onPageClick: (href: string) => void;
}

export const ManageSectionPagesContent: FC<ManageSectionPagesContentProps> = ({
  className,
  scrollAreaClassName,
  onPageClick,
}) => {
  const topicsWithPages = useListSelector(selectVisibleTopicsWithPages);
  const assignmentPages = useListSelector((s) => selectPagesForCurrentSection(s, "assignment"));
  const resourcePages = useListSelector((s) => selectPagesForCurrentSection(s, "course_resource"));
  const sectionKind = useAppSelector(selectCurrentSectionKind);

  const visibleTopicsWithPages = topicsWithPages.filter((topic) => topic.pages.length > 0);

  const hasAnyContent =
    assignmentPages.length > 0 || visibleTopicsWithPages.length > 0 || resourcePages.length > 0;

  return (
    <div className={twMerge("w-full @container/page-metadata", className)}>
      <div className="mb-3 flex items-center gap-3 text-sm text-black-tint-40">
        {t("manage_section_pages_modal.subheader.title", {
          assignments: assignmentPages.length,
          topics: visibleTopicsWithPages.length,
          resources: resourcePages.length,
          pageCount: visibleTopicsWithPages.reduce((sum, topic) => sum + topic.pages.length, 0),
        })}
      </div>

      <div className={twMerge("overflow-y-auto pr-1", scrollAreaClassName)}>
        {!hasAnyContent ? (
          <div className="py-8 text-center text-black-tint-55">
            {t("manage_section_pages_modal.empty")}
          </div>
        ) : (
          <>
            {assignmentPages.length > 0 && (
              <PageAccordionSection
                pages={assignmentPages}
                sectionKind={sectionKind}
                title={
                  <SectionTitle
                    count={assignmentPages.length}
                    icon={<LongFormPencilIcon />}
                    title={t("page_variant_plural.assignment")}
                  />
                }
                onPageClick={onPageClick}
              />
            )}

            {visibleTopicsWithPages.map((topic) => (
              <TopicSection
                key={topic.id}
                sectionKind={sectionKind}
                topic={topic}
                onPageClick={onPageClick}
              />
            ))}

            {resourcePages.length > 0 && (
              <PageAccordionSection
                pages={resourcePages}
                sectionKind={sectionKind}
                title={
                  <SectionTitle
                    count={resourcePages.length}
                    icon={<PaperClipLinkIcon />}
                    title={t("page_variant_plural.course_resource")}
                  />
                }
                onPageClick={onPageClick}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};
